import React from "react";
import { format } from "date-fns";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { AlertTriangle, Wrench, CalendarOff, Edit } from "lucide-react";
import type { ShiftRow } from "./types";
import { driverDisplayName } from "./utils";

export type PublishConflict = {
  shift: ShiftRow;
  reasons: string[];
  kind: "vehicle" | "driver" | "both";
};

interface PublishValidationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  conflicts: PublishConflict[];
  scopeLabel: string; // "All drafts" or "drafts for Tue 15 Apr"
  onReassign: (shift: ShiftRow) => void;
  onConfirmPublishAnyway: () => void;
  confirming: boolean;
}

const PublishValidationDialog: React.FC<PublishValidationDialogProps> = ({
  open,
  onOpenChange,
  conflicts,
  scopeLabel,
  onReassign,
  onConfirmPublishAnyway,
  confirming,
}) => {
  const vehicleCount = conflicts.filter((c) => c.kind !== "driver").length;
  const driverCount = conflicts.filter((c) => c.kind !== "vehicle").length;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl bg-gray-900 border-gray-700 text-white">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-amber-400" />
            Publish Blocked — {conflicts.length} Conflict{conflicts.length === 1 ? "" : "s"}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-3">
          <p className="text-sm text-gray-300">
            The following {scopeLabel} have issues. Resolve them (or publish anyway) before sending next-day allocations.
          </p>

          <div className="flex items-center gap-2 text-xs">
            {vehicleCount > 0 && (
              <Badge variant="outline" className="border-orange-500 text-orange-300">
                <Wrench className="h-3 w-3 mr-1" /> {vehicleCount} vehicle unavailable
              </Badge>
            )}
            {driverCount > 0 && (
              <Badge variant="outline" className="border-red-500 text-red-300">
                <CalendarOff className="h-3 w-3 mr-1" /> {driverCount} driver on approved time off
              </Badge>
            )}
          </div>

          <div className="max-h-80 overflow-y-auto space-y-2 pr-1">
            {conflicts.map((c) => (
              <div
                key={c.shift.id}
                className="rounded-md border border-red-700/40 bg-red-950/20 p-3 flex items-start justify-between gap-3"
              >
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold text-white truncate">
                    {driverDisplayName(c.shift)}
                    {c.shift.vehicle_number && (
                      <span className="text-gray-400 font-normal ml-1">• {c.shift.vehicle_number}</span>
                    )}
                  </p>
                  <p className="text-xs text-gray-400">
                    {format(new Date(`${c.shift.shift_date}T00:00:00`), "EEE d MMM")} •{" "}
                    {c.shift.start_hhmm}–{c.shift.end_hhmm} • {c.shift.shift_type_label}
                  </p>
                  <ul className="mt-1.5 space-y-0.5">
                    {c.reasons.map((r, i) => (
                      <li key={i} className="text-[11px] text-red-300 flex items-center gap-1.5">
                        <AlertTriangle className="h-3 w-3 shrink-0" />
                        {r}
                      </li>
                    ))}
                  </ul>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  className="border-blue-600 text-blue-300 hover:bg-blue-900/30 shrink-0"
                  onClick={() => onReassign(c.shift)}
                >
                  <Edit className="h-3.5 w-3.5 mr-1" /> Reassign
                </Button>
              </div>
            ))}
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            variant="outline"
            className="border-amber-600 text-amber-300 hover:bg-amber-900/30"
            onClick={onConfirmPublishAnyway}
            disabled={confirming}
          >
            {confirming ? "Publishing…" : "Publish Anyway"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default PublishValidationDialog;
